import { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, CameraOff } from 'lucide-react';
import { cameraFailureMessage, reportCameraFailure } from '@/lib/reportCameraFailure';
import { reportScannerEvent, type ScannerSource } from '@/lib/reportScannerEvent';
import { useKioskSession } from '@/lib/useKiosk';
import {
  canUseNativeBarcodeScanner,
  isNativeScanCancellation,
  scanNativeBarcode,
} from '../lib/nativeBarcodeScanner';

const HAS_BARCODE_DETECTOR = 'BarcodeDetector' in window;

interface NativeBarcode { rawValue: string }
interface NativeBarcodeDetector {
  detect(source: HTMLCanvasElement): Promise<NativeBarcode[]>;
}
interface NativeBarcodeDetectorCtor {
  new (options?: { formats?: string[] }): NativeBarcodeDetector;
}

export type BarcodeScanResult = {
  source: ScannerSource;
  decodedAt: number;
};

// Module-level singleton — WASM initialises once, all scans share it.
let zxingReaderPromise: Promise<(img: ImageData) => Promise<string | null>> | null = null;

async function loadZxingReader() {
  if (!zxingReaderPromise) {
    zxingReaderPromise = (async () => {
      const [{ readBarcodes, prepareZXingModule }, { default: wasmUrl }] = await Promise.all([
        import('zxing-wasm/reader'),
        import('zxing-wasm/reader/zxing_reader.wasm?url'),
      ]);
      await prepareZXingModule({
        overrides: { locateFile: () => wasmUrl as string },
        fireImmediately: true,
      });
      return async (img: ImageData): Promise<string | null> => {
        const results = await readBarcodes(img, {
          formats: ['EAN13', 'EAN8', 'UPCA', 'UPCE', 'Code128', 'Code39', 'QRCode'],
          tryHarder: true,
          maxNumberOfSymbols: 1,
        });
        const hit = results.find((r) => r.isValid && r.text);
        return hit?.text ?? null;
      };
    })();
  }
  return zxingReaderPromise;
}

export function BarcodeScanner({
  onBarcodeFound,
  storeNo,
}: {
  onBarcodeFound: (upc: string, result: BarcodeScanResult) => void;
  /** Store the scan belongs to, for the failure report. */
  storeNo?: number | string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const scanningRef = useRef(false);
  const scanStartedAtRef = useRef<number | null>(null);

  const [enabled, setEnabled] = useState(false);
  const [nativeScanning, setNativeScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Holds the guidance to show when the camera cannot be offered at all, so the
  // Scan button is replaced rather than sitting there inviting a retry that
  // cannot work (a blocked permission, or no camera).
  const [cameraUnavailable, setCameraUnavailable] = useState<string | null>(null);

  // A failure on an enrolled tablet is worth far more with the device on it:
  // it is what ties a report to a physical store fixture. Cached app-wide, and
  // a personal Google sign-in simply has no kiosk session.
  const device = useKioskSession().data?.device ?? null;
  const nativeScannerAvailable = canUseNativeBarcodeScanner();

  const reportScanner = useCallback((
    type: Parameters<typeof reportScannerEvent>[0]['type'],
    source: ScannerSource,
    extra: Pick<Parameters<typeof reportScannerEvent>[0], 'barcodeLength' | 'durationMs'> = {},
  ) => {
    reportScannerEvent({
      type,
      source,
      storeNo: storeNo ?? device?.store_no,
      deviceId: device?.id,
      ...extra,
    });
  }, [storeNo, device]);

  const reportDecoded = useCallback((barcode: string, source: ScannerSource) => {
    const decodedAt = Date.now();
    reportScanner('decoded', source, {
      barcodeLength: barcode.length,
      ...(scanStartedAtRef.current !== null
        ? { durationMs: decodedAt - scanStartedAtRef.current }
        : {}),
    });
    scanStartedAtRef.current = null;
    onBarcodeFound(barcode, { source, decodedAt });
  }, [onBarcodeFound, reportScanner]);

  const stopScan = useCallback(() => {
    scanningRef.current = false;
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const startScan = useCallback(async () => {
    setError(null);
    const source: ScannerSource = HAS_BARCODE_DETECTOR ? 'browser-detector' : 'browser-zxing';
    scanStartedAtRef.current = Date.now();
    reportScanner('started', source);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;

      const video = videoRef.current;
      if (!video) { stopScan(); return; }
      video.srcObject = stream;
      await video.play();

      const canvas = canvasRef.current;
      if (!canvas) { stopScan(); return; }

      if (HAS_BARCODE_DETECTOR) {
        const BC = (window as unknown as { BarcodeDetector: NativeBarcodeDetectorCtor }).BarcodeDetector;
        const detector = new BC({ formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39'] });
        scanningRef.current = true;

        const loop = async () => {
          if (!scanningRef.current) return;
          canvas.width = video.videoWidth || 320;
          canvas.height = video.videoHeight || 240;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            try {
              const barcodes = await detector.detect(canvas);
              if (barcodes[0]) {
                stopScan();
                setEnabled(false);
                reportDecoded(barcodes[0].rawValue, source);
                return;
              }
            } catch { /* ignore per-frame detection errors */ }
          }
          rafRef.current = requestAnimationFrame(loop);
        };
        rafRef.current = requestAnimationFrame(loop);
      } else {
        // zxing-wasm fallback: mark active before the async WASM load so stopScan() can abort it.
        scanningRef.current = true;
        let readFn: ((img: ImageData) => Promise<string | null>) | null = null;
        try {
          readFn = await loadZxingReader();
        } catch (e) {
          reportCameraFailure({
            errorName: 'DecoderLoadFailed',
            errorMessage: e instanceof Error ? e.message : String(e),
            storeNo: storeNo ?? device?.store_no,
            deviceId: device?.id,
          });
          setError(cameraFailureMessage('DecoderLoadFailed'));
          stopScan();
          setEnabled(false);
          return;
        }
        if (!scanningRef.current) return; // user stopped while WASM was loading

        const capturedReadFn = readFn;
        const loop = async () => {
          if (!scanningRef.current) return;
          canvas.width = video.videoWidth || 320;
          canvas.height = video.videoHeight || 240;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            try {
              const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
              const text = await capturedReadFn(imageData);
              if (text) {
                stopScan();
                setEnabled(false);
                reportDecoded(text, source);
                return;
              }
            } catch { /* ignore per-frame decode errors */ }
          }
          if (scanningRef.current) rafRef.current = requestAnimationFrame(loop);
        };
        rafRef.current = requestAnimationFrame(loop);
      }
    } catch (e) {
      const name = e instanceof Error && e.name ? e.name : 'CameraError';
      reportCameraFailure({
        errorName: name,
        errorMessage: e instanceof Error ? e.message : String(e),
        storeNo: storeNo ?? device?.store_no,
        deviceId: device?.id,
      });
      const message = cameraFailureMessage(name);
      if (name === 'NotAllowedError' || name === 'NotFoundError' || name === 'OverconstrainedError') {
        setCameraUnavailable(message);
        setEnabled(false);
      } else {
        setError(message);
      }
      stopScan();
      scanStartedAtRef.current = null;
    }
  }, [reportDecoded, reportScanner, stopScan, storeNo, device]);

  const startNativeScan = useCallback(async () => {
    setError(null);
    setNativeScanning(true);
    scanStartedAtRef.current = Date.now();
    reportScanner('started', 'native');
    try {
      const barcode = await scanNativeBarcode();
      if (barcode) {
        reportDecoded(barcode, 'native');
      } else {
        reportScanner('cancelled', 'native', {
          durationMs: Date.now() - scanStartedAtRef.current,
        });
      }
    } catch (e) {
      if (isNativeScanCancellation(e)) {
        reportScanner('cancelled', 'native', {
          ...(scanStartedAtRef.current !== null
            ? { durationMs: Date.now() - scanStartedAtRef.current }
            : {}),
        });
      } else {
        console.error('[sweetspot-barcode-scanner] Native scanner failed', e);
        const candidate = e as { code?: unknown; message?: unknown };
        const details = [candidate?.code, candidate?.message]
          .filter((value) => typeof value === 'string' && value.length > 0)
          .join(': ');
        reportCameraFailure({
          errorName: 'NativeScanFailed',
          errorMessage: details || String(e),
          storeNo: storeNo ?? device?.store_no,
          deviceId: device?.id,
        });
        setError(cameraFailureMessage('NativeScanFailed'));
      }
    } finally {
      scanStartedAtRef.current = null;
      setNativeScanning(false);
    }
  }, [reportDecoded, reportScanner, storeNo, device]);

  useEffect(() => {
    if (enabled) {
      void startScan();
    } else {
      stopScan();
    }
    return () => stopScan();
  }, [enabled, startScan, stopScan]);

  if (cameraUnavailable) {
    return (
      <div className="mb-4 rounded-xl border border-candy-line bg-candy-white p-4 shadow-1 dark:border-candy-dark-line dark:bg-candy-dark-surface">
        <p className="text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">
          {cameraUnavailable}
        </p>
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-xl border border-candy-line bg-candy-white p-4 shadow-1 dark:border-candy-dark-line dark:bg-candy-dark-surface">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-medium text-candy-ink dark:text-candy-dark-ink">Barcode scanner</span>
        <button
          type="button"
          onClick={() => {
            if (nativeScannerAvailable) {
              void startNativeScan();
            } else {
              if (enabled) {
                const source: ScannerSource = HAS_BARCODE_DETECTOR ? 'browser-detector' : 'browser-zxing';
                reportScanner('cancelled', source, {
                  ...(scanStartedAtRef.current !== null
                    ? { durationMs: Date.now() - scanStartedAtRef.current }
                    : {}),
                });
                scanStartedAtRef.current = null;
                setEnabled(false);
              } else {
                setEnabled(true);
              }
            }
          }}
          disabled={nativeScanning}
          className="inline-flex items-center gap-1.5 rounded-md border border-candy-line px-3 py-1.5 text-xs font-medium text-candy-ink hover:border-candy-pink dark:border-candy-dark-line dark:text-candy-dark-ink dark:hover:border-candy-pink-dark"
        >
          {!nativeScannerAvailable && enabled ? (
            <><CameraOff size={13} aria-hidden /> Stop</>
          ) : (
            <><Camera size={13} aria-hidden /> {nativeScanning ? 'Scanning...' : 'Scan'}</>
          )}
        </button>
      </div>
      {error && <p className="mb-2 text-xs text-candy-pink dark:text-candy-pink-dark">{error}</p>}
      <div className={!nativeScannerAvailable && enabled ? 'block' : 'hidden'}>
        <video
          ref={videoRef}
          muted
          playsInline
          className="h-48 w-full rounded-lg bg-black object-cover"
          aria-label="Camera feed for barcode scanning"
        />
        <canvas ref={canvasRef} className="hidden" aria-hidden />
        <p className="mt-1.5 text-center text-xs text-candy-ink-muted dark:text-candy-dark-ink-muted">
          Point camera at a barcode
        </p>
      </div>
    </div>
  );
}
